export interface CaseStudySection {
  title: string
  bullets: string[]
}

export interface CaseStudy {
  id: string
  title: string
  subtitle: string
  description: string
  metrics: { label: string; value: string }[]
  role: string
  timeline: string
  sections: CaseStudySection[]
}

export const caseStudies: CaseStudy[] = [
  {
    id: 'copilot-voice',
    title: 'Copilot Voice',
    subtitle:
      'Doubling D7 retention through prompt optimization, LLM evals, and UX iteration',
    description:
      'Owned the voice experience in Copilot for Edge. Discovered that high eval scores masked real user dissatisfaction, rebuilt the evaluation framework, and drove prompt engineering and UX changes that doubled retention.',
    metrics: [
      { label: 'D7 Retention', value: '2×' },
      { label: 'Negative Feedback', value: '-10%' },
    ],
    role: 'Product Manager',
    timeline: '2023 – Present',
    sections: [
      {
        title: 'The Problem',
        bullets: [
          'D7 retention at only 6% despite strong early adoption',
          'Eval scores showed 85% helpfulness — but users were still leaving',
          'Disconnect: evals rewarded conversational style, but Edge users wanted concise, task-focused answers',
        ],
      },
      {
        title: 'The Insight',
        bullets: [
          'User research revealed Edge users are task-focused — they want direct answers, not conversation',
          'Generic evals were rewarding the exact behavior users were leaving over',
          'One-size-fits-all Copilot personality was hurting Edge-specific retention',
        ],
      },
      {
        title: 'Execution',
        bullets: [
          'Proposed shifting Voice responses to be concise and task-focused',
          'Navigated pushback from partner teams using user research evidence',
          'Rebuilt eval pipeline from scratch — voice-specific scenarios, real customer feedback, no synthetic benchmarks',
          'Manually clustered complaints, identified failure patterns, and tagged them for the new eval dataset',
          'Ran tight measure-deploy-iterate loop on prompt tuning and UX adjustments',
        ],
      },
      {
        title: 'The Result',
        bullets: [
          'Doubled D7 Voice retention (2×)',
          'Reduced negative user feedback by 10%',
          'Shifted team from shipping against misleading metrics to a feedback-informed eval system',
        ],
      },
    ],
  },
  {
    id: 'copilot-chat-growth',
    title: 'Copilot Chat Growth',
    subtitle:
      'Growing Edge Copilot Chat weekly active users by 1M through funnel optimization',
    description:
      'Identified and fixed two massive drop-offs in the Copilot Chat funnel in Edge, driving 800K+ incremental WAU through CTA simplification and landing experience redesign.',
    metrics: [{ label: 'Incremental WAU', value: '1M+' }],
    role: 'Product Manager',
    timeline: '2023 – 2024',
    sections: [
      {
        title: 'The Problem',
        bullets: [
          'Aggressive target: 1M WAU for Copilot Chat in Edge',
          'Two massive funnel drop-offs blocking growth',
          'Only 5% of Edge users clicking on Copilot; only 40% of clickers sent a first message',
        ],
      },
      {
        title: 'The Insight',
        bullets: [
          'CTA branded "Copilot" with no clear value prop — users fatigued by AI branding',
          'Landing experience was a cluttered news feed, not a useful tool',
          'Users needed to see immediate value tied to their current browser task',
        ],
      },
      {
        title: 'Execution',
        bullets: [
          'Simplified CTA to "Chat" — lowered cognitive barrier to entry',
          'Replaced news feed with a clean, focused panel oriented toward first meaningful action',
          'Identified in-browser moments of need (e.g., failed Ctrl+F) to surface Copilot as a natural fallback',
        ],
      },
      {
        title: 'The Result',
        bullets: [
          'Drove 800K+ incremental WAU — hit the 1M target',
          'Ctrl+F fallback integration alone added 40K WAU with healthy retention',
        ],
      },
    ],
  },
  {
    id: 'copilot-chat-launch',
    title: 'Copilot Chat 0-to-1 Launch',
    subtitle:
      'Shipping a redesigned chat experience to 300K+ users across Windows and Mac',
    description:
      'Led the 0-to-1 launch of the redesigned Copilot Chat surface in Edge, clearing 112+ quality blockers across privacy and accessibility to reach GA readiness.',
    metrics: [
      { label: 'Users at Launch', value: '300K+' },
      { label: 'Blockers Cleared', value: '112+' },
    ],
    role: 'Product Manager',
    timeline: '2024',
    sections: [
      {
        title: 'The Problem',
        bullets: [
          'Existing chat experience was fragmented across platforms',
          'Needed to unify voice and text into a single modality-agnostic experience',
          'GA expectations required clearing significant quality, privacy, and accessibility gaps',
        ],
      },
      {
        title: 'The Approach',
        bullets: [
          'Set the vision: chat as a seamless, native experience — not a bolted-on feature',
          'Aligned engineering, design, and platform teams around shared architecture and roadmap',
        ],
      },
      {
        title: 'Execution',
        bullets: [
          'Defined roadmap and ship criteria for GA readiness',
          'Drove cross-team alignment across Engineering, Design, Platform, Legal, and Accessibility',
          'Triaged and resolved 112+ quality blockers across privacy and accessibility',
        ],
      },
      {
        title: 'The Result',
        bullets: [
          'Shipped redesigned experience to 300K+ users on Windows and Mac',
          'Established the foundation for the unified chat + voice experience serving millions daily',
        ],
      },
    ],
  },
  {
    id: 'copilot-mode-chat',
    title: 'Copilot Mode Chat Interface',
    subtitle:
      'Evaluating Product-Market Fit for a redesigned immersive chat experience',
    description:
      'Led the redesign and evaluation of a new chat interface for Copilot Mode, testing with 1M users to gather critical product-market fit signals.',
    metrics: [
      { label: 'Test Audience', value: '1M Users' },
      { label: 'Signal Gathered', value: 'High' },
    ],
    role: 'Product Manager',
    timeline: '2024',
    sections: [
      {
        title: 'The Problem',
        bullets: [
          'Needed to validate whether an immersive, simplified chat interface would drive higher engagement',
          'Standard sidebar implementation had limitations for deep AI interactions',
        ],
      },
      {
        title: 'The Approach',
        bullets: [
          'Designed a clean "Mode" that removed browser chrome and centered the AI interaction',
          'Drove strategy for a large-scale A/B test with strict telemetry',
          'Measured task completion and satisfaction — not just usage',
        ],
      },
      {
        title: 'Execution',
        bullets: [
          'Managed cross-functional execution across design, engineering, and data science',
          'Rolled out to a 1M user cohort with rigorous measurement',
        ],
      },
      {
        title: 'The Result',
        bullets: [
          'Design did not meet strict criteria for full rollout — remained unshipped',
          'Insights directly informed the next generation of core UI',
          "Proved the value of rigorous, large-scale experimentation over shipping for shipping's sake",
        ],
      },
    ],
  },
  {
    id: 'text-prediction',
    title: 'Text Prediction in Edge',
    subtitle: 'Scaling an AI/ML writing assistant to 1.1M daily active users',
    description:
      'Owned the roadmap and quality iteration for AI-powered Text Prediction in Edge, scaling it from early access to over a million daily active users.',
    metrics: [{ label: 'Daily Active Users', value: '1.1M' }],
    role: 'Product Manager',
    timeline: '2022 – 2023',
    sections: [
      {
        title: 'The Problem',
        bullets: [
          'Text Prediction had early access traction but no clear path to scale',
          'Needed to work reliably across diverse input contexts while maintaining low latency',
        ],
      },
      {
        title: 'The Approach',
        bullets: [
          'Owned roadmap prioritization, ship criteria, and iterative quality improvements',
          'Balanced model performance, user experience, and rollout safety at every stage',
        ],
      },
      {
        title: 'Execution',
        bullets: [
          'Led quality iteration in partnership with Engineering',
          'Ran experiments to validate improvements before broader rollout',
          'Built foundation for shipping ML-powered experiences at scale',
        ],
      },
      {
        title: 'The Result',
        bullets: [
          'Scaled to 1.1M Daily Active Users',
          'Established Text Prediction as a core AI feature in Edge',
        ],
      },
    ],
  },
  {
    id: 'shopee-kyc',
    title: 'Shopee KYC Platform',
    subtitle: 'Building centralized seller verification across Southeast Asia',
    description:
      'Designed and launched a centralized KYC verification platform at Shopee, serving 9K+ sellers across multiple Southeast Asian markets.',
    metrics: [
      { label: 'Sellers Served', value: '9K+' },
      { label: 'Markets', value: 'SE Asia' },
    ],
    role: 'Product Manager',
    timeline: '2019 – 2021',
    sections: [
      {
        title: 'The Problem',
        bullets: [
          'Seller verification was fragmented across markets — duplicated data, inconsistent checks',
          'Small business owners with no IT support faced a confusing onboarding process',
        ],
      },
      {
        title: 'The Approach',
        bullets: [
          'Designed a centralized platform improving onboarding integrity at scale',
          'Aligned stakeholders across multiple markets for shared data and workflows',
        ],
      },
      {
        title: 'Execution',
        bullets: [
          'Launched centralized KYC platform standardizing verification across markets',
          'Accommodated market-specific regulatory requirements',
          'Built access control system for 24,000 employees',
        ],
      },
      {
        title: 'The Result',
        bullets: [
          'Served 9K+ sellers across Southeast Asia',
          'Reduced data duplication and improved verification consistency at scale',
        ],
      },
    ],
  },
  {
    id: 'shecodes-vietnam',
    title: 'SheCodes Vietnam',
    subtitle:
      'Building a 10K-member tech community from scratch with $0 marketing',
    description:
      "Founded and scaled a women-in-tech nonprofit from zero, growing to 10,000 members organically, raising $30K, and launching Southeast Asia's first all-female hackathon.",
    metrics: [
      { label: 'Community Members', value: '10K+' },
      { label: 'Recognition', value: 'UN Women' },
      { label: 'Funds Raised', value: '$30K' },
    ],
    role: 'Founder & Director',
    timeline: '2018 – 2022',
    sections: [
      {
        title: 'The Problem',
        bullets: [
          "Vietnam's growing tech sector had severe gender imbalance",
          'Women lacked access to supportive communities and practical tech education',
        ],
      },
      {
        title: 'The Approach',
        bullets: [
          'Created a safe, encouraging environment for women to learn coding',
          'Started with a single hackathon, expanded into a comprehensive educational platform',
          'Zero marketing budget — grew entirely through community and word of mouth',
        ],
      },
      {
        title: 'Execution',
        bullets: [
          'Led a team of 50+ volunteers',
          'Secured sponsorships from major tech companies, raising $30K total',
          "Launched Southeast Asia's first all-female hackathon",
          'Designed curriculum for coding bootcamps',
        ],
      },
      {
        title: 'The Result',
        bullets: [
          'Grew to 10K members organically in 2 months with $0 marketing',
          'Received Ambassador for Gender Equality award from UN Women (2020)',
        ],
      },
    ],
  },
]
